import user from 'js/lib/user';

import { useNaptime } from 'bundles/naptimejs';
import ProfileConsentsV1 from 'bundles/naptimejs/resources/profileConsents.v1';
import { PROFILE_CONSENT_COOKIE_CONSENT_NAMES } from 'bundles/user-consent/constants';
import useEnterpriseUserInfo from 'bundles/user-consent/hooks/useEnterpriseUserInfo';
import type { ProfileConsent } from 'bundles/user-consent/types';
import { isMissingCookieConsent } from 'bundles/user-consent/utils';

type ProfileConsentData = {
  profileConsents?: ProfileConsent;
};

const useProfileConsents = (userId: number, shouldSkipQuery = false) => {
  const {
    data: { profileConsents },
    pending: isProfileConsentsLoading,
  } = useNaptime<ProfileConsentData>(() => {
    const isMissingCCPAConsentCookie = isMissingCookieConsent(PROFILE_CONSENT_COOKIE_CONSENT_NAMES.ccpaRequired);
    const isMissingGDPRConsentCookie = isMissingCookieConsent(PROFILE_CONSENT_COOKIE_CONSENT_NAMES.gdprRequired);

    if (shouldSkipQuery || (!isMissingCCPAConsentCookie && !isMissingGDPRConsentCookie)) {
      return {};
    }

    return {
      profileConsents: ProfileConsentsV1.get(userId, {
        fields: [
          ...(isMissingGDPRConsentCookie ? ['requireGdprConsent'] : []),
          ...(isMissingCCPAConsentCookie ? ['requireCcpaConsent'] : []),
        ],
      }),
    };
  }, [userId]);

  return { profileConsents, isProfileConsentsLoading };
};

/**
 * Custom hook to fetch and determine marketing consent data for a user.
 *
 * This hook fetches profile consent data and enterprise user information for a given user.
 * It determines whether CCPA and GDPR marketing consents should be shown, considering the user's
 * profile consents and enterprise user restrictions.
 *
 * @param {number} [userAccountId] - The user account ID. If not provided, the ID of the currently authenticated user is used.
 * @param {boolean} [shouldSkipQuery=false] - Flag indicating whether the query should be skipped.
 *
 * @returns {Object} Hook return values.
 * @returns {boolean} shouldShowCCPAMarketingConsent - Indicates whether the CCPA marketing consent should be shown.
 * @returns {boolean} shouldShowGDPRMarketingConsent - Indicates whether the GDPR marketing consent should be shown.
 * @returns {boolean} isEnterpriseAdmin - Indicates whether the user is an enterprise admin.
 * @returns {boolean} isLoading - Indicates whether the data fetching is still in progress.
 */
const useMarketingConsentData = (userAccountId?: number, shouldSkipQuery = false) => {
  const userId = userAccountId || user.get().id;
  const { profileConsents, isProfileConsentsLoading } = useProfileConsents(userId, shouldSkipQuery);
  const requireCCPAConsent = !!profileConsents?.requireCcpaConsent;
  const requireGDPRConsent = !!profileConsents?.requireGdprConsent;
  const shouldSkipEnterpriseQuery = shouldSkipQuery || (!requireCCPAConsent && !requireGDPRConsent);
  const {
    isEnterpriseAdmin,
    isRestrictedEnterpriseUser,
    isLoading: isEnterpriseUserInfoLoading,
  } = useEnterpriseUserInfo(shouldSkipEnterpriseQuery, userId);
  const shouldShowCCPAMarketingConsent = !!(requireCCPAConsent && !isRestrictedEnterpriseUser);
  const shouldShowGDPRMarketingConsent = !!(requireGDPRConsent && !isRestrictedEnterpriseUser);
  const isLoading = isProfileConsentsLoading || isEnterpriseUserInfoLoading;

  return { shouldShowCCPAMarketingConsent, shouldShowGDPRMarketingConsent, isEnterpriseAdmin, isLoading };
};

export default useMarketingConsentData;
